import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { useReservationData } from "./hooks/useReservationData";
import { useReservationForm } from "./hooks/useReservationForm";
import { useReservationPayload } from "./hooks/useReservationPayload";
import { FinalizeErrorCode, isOlympiacosAccessError, useSubmitReservation } from "./hooks/useSubmitReservation";
import { useFieldErrorMapper } from "./hooks/useFieldErrorMapper";
import { useReservationModals } from "./hooks/useReservationModals";
import { useLeaveGuard } from "./hooks/useLeaveGuard";
import { ContactInformation } from "../../components/reservation-page/contact-information/contact-information";
import { ShippingAddress } from "../../components/reservation-page/shipping-address/shipping-address";
import {
  MoreTheme,
  OlympiacosTheme,
  PaobcTheme,
} from "../../components/common/mixins";
import { Donations } from "../../components/reservation-page/donations";
import { AgreementCheckboxes } from "../../components/reservation-page/agreement-checkboxes";
import { OrderInfo } from "../../components/reservation-page/order-info";
import { Divider } from "../../common";
import { useReservationStore } from "../../stores/useReservationStore";
import { OrderStatus } from "../../common/enums/OrderStatus";
import { Products } from "../../components/reservation-page/products";
import { useExpirationStatus } from "./hooks/useCountdown";
import { CountdownDisplay } from "./CountdownDisplay";
import { TicketInfo } from "../../components/reservation-page/ticket-holders";
import { useUserData } from "../../hooks/useUserData";
import { isDonationActive } from "./utils/donationUtils";
import ReservationSkeleton from "../../components/Skeletons/ReservationPageSkeleton";
import {
  PageLayout,
  LeftColumn,
  RightColumn,
    ReservationPageScopedGlobalStyle,
    OlympiacosReservationScopedGlobalStyle,
  BREAKPOINT_RESERVATION_PAGE,
  PageContainer,
  TopContainer,
  BottomContainer,
  Actions,
  SummaryDrawerScrollable,
  SummaryDrawerContent,
  SummaryDrawerPuller,
} from "../reservation-shared-styles";
import { ErrorPage } from "../../components/common/ErrorPage";
import { PaymentButtons } from "./PaymentButtons";
import { useCreatePayHandler } from "./hooks/useCreatePayHandler";
import { useOrderStatusRedirect } from "./hooks/useOrderStatusRedirect";
import { useFormSessionRestore } from "./hooks/useFormSessionRestore";
import { ReservationBottomBar } from "./ReservationBottomBar";

import useModal from "../../hooks/useModal";
import { useOrderTotal } from "./hooks/useOrderTotal";
import {
  getAttachmentUrl,
  getPlayUrl,
  getCulture,
  mainUrl,
  getLocaleSegment,
} from "../../utils/Utils";
import { apiDelete } from "../../api/fetch";
import { endpoints } from "../../utils/Constants";
import { getReservationKeyFromPath } from "../../components/Utils";
import { PRODUCER_ID_KOK, PRODUCER_ID_OLYMPIACOS } from "../../common/producerIds";
import { getReservationDisplayDate } from "./utils/getReservationDisplayDate";
import { SwipeableDrawer, useMediaQuery } from "@mui/material";
import { Billing } from "../../components/reservation-page/billing";
import { usePaymentMethods } from "./hooks/usePaymentMethods";
import { PaymentOverlay } from "./PaymentOverlay";
import { useReservationResources } from "./hooks/useReservationResources";
import { formatClientMinimumQuantityMessage } from "./hooks/finalizeErrorMessages";
import {
  getMinimumViolations,
  getTicketNumberMap,
} from "./utils/productQuantities";
import { useMaybeUpdateUser } from "../../hooks/useMaybeUpdateUser";
import { ReservationModals } from "./ReservationModals";
import useMiniSite from "../../hooks/useMiniSite";
import SitePolicyPrompt from "../../components/SitePolicyPrompt.jsx";
import { useScrollToFirstError } from "./hooks/useScrollToFirstError";
import { useIntercomEventAttributes } from "../../hooks/useIntercomEventAttributes";
import { IntercomBelowModalsGlobalStyle } from "../../components/common/IntercomBelowModalsGlobalStyle";

export {
  PageLayout,
  LeftColumn,
  RightColumn,
} from "../reservation-shared-styles";

// ErrorPage retry remounts the page via `key` so form, modal and submit state
// start fresh and data is refetched. Unlike a full reload, the global reservation
// store (e.g. isAgeVerified) survives until the new fetch resets it.
export function ReservationApp() {
  const [attempt, setAttempt] = useState(0);
  return (
    <ReservationPageContent
      key={attempt}
      onRetry={() => setAttempt((n) => n + 1)}
    />
  );
}

function ReservationPageContent({ onRetry }: { onRetry: () => void }) {
  const products = useReservationStore((s) => s.products);
  const orderedTicketIds = useReservationStore((s) => s.orderedTicketIds);
  const requestCloseProductOverlays = useReservationStore(
    (s) => s.requestCloseProductOverlays,
  );
  const { userData, isLoggedIn, loading: userLoading } = useUserData();
  const {
    data,
    loading: reservationLoading,
    error,
    refetch,
  } = useReservationData();

  useIntercomEventAttributes({ eventId: data?.eventId, playId: data?.playId });

  const { isOlympiacos, isPaoBC } = useMiniSite();
  const isUnder900 = useMediaQuery(
    `(max-width: ${BREAKPOINT_RESERVATION_PAGE})`,
  );

  const { maybeUpdateUser } = useMaybeUpdateUser(
    userData,
    isLoggedIn,
  );

  const r = useReservationResources();

  const tickets = data?.tickets ?? [];
  const ticketPersonFormFields = data?.ticketPersonFormFields ?? [];
  const isKokEvent =
    data?.producer?.id === PRODUCER_ID_KOK &&
    ticketPersonFormFields.some((f) => f.ticketPersonMap === "Age");
  const isOlympiacosEvent = data?.producer?.id === PRODUCER_ID_OLYMPIACOS;
  const merchantTerms = data?.merchantTerms ?? [];
  const donationCampaign = data?.donationCampaign;
  const donationActive = isDonationActive(donationCampaign);

  const methods = useReservationForm({
    userData,
    ticketPersonFormFields,
    tickets,
    merchantTerms,
    donationCampaign,
    isKokEvent,
    producerId: data?.producer?.id ?? null,
  });

  const expiresAt = data?.expiresAt ?? "";
  const { isExpired, showTimer } = useExpirationStatus(expiresAt);

  const venue = data?.venue;
  const playTitle = data?.playTitle;
  const showBilling = data?.allowInvoicing && data?.totalPrice !== 0;
  const date = getReservationDisplayDate(data, getCulture());

  const status = data?.status;

  const playUrl = data?.playUrl
    ? getPlayUrl(data.playUrl)
    : `${mainUrl()}/${getLocaleSegment()}/`;

  const imageUrl = data?.eventImage?.imageUrl
    ? getAttachmentUrl(data.eventImage.imageUrl)
    : null;

  const producerName = data?.producer?.name;
  const currency = data?.currency;
  const hasPerTicketProducts = products.some((p) => p.isPerTicket);
  const hasPerOrderProducts = products.some((p) => !p.isPerTicket);
  const paymentMethods = data?.paymentMethods ?? [];
  const returnUrl = playUrl ? playUrl : "/";
  const { isRedirecting } = useOrderStatusRedirect(status, returnUrl, data?.amountDue);

  const shippingFee = methods.watch("shippingFee");
  const donationAmount = methods.watch("donationAmount");
  const total = useOrderTotal(data?.totalPrice ?? 0, donationAmount, shippingFee);

  const { buildPayload } = useReservationPayload(tickets, ticketPersonFormFields);
  const {
    submitReservation,
    isSubmitting: isSubmittingReservation,
    error: submitError,
    clearError: clearSubmitError,
  } = useSubmitReservation(isKokEvent);

  const {
    isOpen: isProductsUpdatedModalOpen,
    openModal: openProductsUpdatedModal,
    closeModal: closeProductsUpdatedModal,
  } = useModal(false, { disableScrollLock: true });

  const {
    isOpen: isProductMinQuantityModalOpen,
    openModal: openProductMinQuantityModal,
    closeModal: closeProductMinQuantityModal,
  } = useModal(false, { disableScrollLock: true });

  const {
    isOpen: isEmailLimitModalOpen,
    openModal: openEmailLimitModal,
    closeModal: closeEmailLimitModal,
  } = useModal(false, { disableScrollLock: true });

    const {
        expirationModalOpen,
        setExpirationModalOpen,
        handleCloseExpirationModal,
        isPaymentModalOpen,
        openPaymentModal,
        closePaymentModal,
        paymentModalKey,
        setPaymentModalKey,
        isOrderSummaryOpen,
        openOrderSummary,
        closeOrderSummary,
        isGenericErrorModalOpen,
        openGenericErrorModal,
        handleCloseGenericErrorModal,
    } = useReservationModals({
        playUrl,
        onCloseGenericError: clearSubmitError,
    });

    const { isLeaveModalOpen, closeLeaveModal, handleLeave, markAsLeaving } = useLeaveGuard({
        showTimer,
        expirationModalOpen,
        playUrl,
        onBeforeOpen: () => {
            closePaymentModal();
            closeOrderSummary();
            handleCloseGenericErrorModal();
            closeProductsUpdatedModal();
            closeProductMinQuantityModal();
            closeEmailLimitModal();
            requestCloseProductOverlays();
        },
        suppressSelector: ".language-selector-menu-item, .olympiacos-lang-option",
        guardEntries: [
            { selector: "#user_container", condition: () => window.matchMedia("(max-width: 768px)").matches },
            { selector: "#notification-bell", condition: () => window.matchMedia("(max-width: 600px)").matches },
        ],
        isSuppressed: isSubmittingReservation,
    });

  useEffect(() => {
    document.body.style.cursor = isSubmittingReservation ? "wait" : "";
    return () => {
      document.body.style.cursor = "";
    };
  }, [isSubmittingReservation]);

  useScrollToFirstError(methods.formState.submitCount);

  useFormSessionRestore({
    reset: methods.reset,
  });

  const isProductError = (code: number | undefined) =>
    code !== undefined && code >= 40000 && code < 50000;

  const isFieldMappedError = (err: typeof submitError) =>
    !!(err?.perFieldErrors && Object.keys(err.perFieldErrors).length > 0);


  const shouldShowGenericError = (err: typeof submitError) =>
    !!err &&
    !isProductError(err.errorCode) &&
    !isFieldMappedError(err) &&
    !isOlympiacosAccessError(err.errorCode) &&
    err.errorCode !== FinalizeErrorCode.EmailTicketLimitReached;


  const handleProductError = async () => {
    // On failure refetch sets error → ErrorPage; don't open this modal behind it.
    if (await refetch()) openProductsUpdatedModal();
  };

  const handleSubmitError = async (err: typeof submitError) => {
    if (err?.errorCode === FinalizeErrorCode.ProductMinimumQuantityNotMet) {
      // User just needs to add more of a product. Nothing changed server-side,
      // so don't refetch — that's the products-updated path.
      openProductMinQuantityModal();
    } else if (err?.errorCode === FinalizeErrorCode.EmailTicketLimitReached) {
      openEmailLimitModal();
    } else if (isProductError(err?.errorCode)) {
      await handleProductError();
    } else if (isOlympiacosAccessError(err?.errorCode)) {
      sitePolicyPromptRef.current?.open();
    } else if (shouldShowGenericError(err)) {
      openGenericErrorModal();
    }
  };

  useFieldErrorMapper(submitError, methods);

  const sitePolicyPromptRef = useRef<{ open: (reason?: string) => void }>(null);

  // Client-side mirror of the server's 40007 check (r.ProductMinimumQuantityNotMet).
  // The server stays authoritative and re-validates on submit regardless — this
  // only spares the customer a doomed round-trip when a per-ticket product's
  // minimum is still unmet.
  const [preCheckMinQuantityMessage, setPreCheckMinQuantityMessage] = useState("");

  const preValidateMinimums = (): boolean => {
    const violations = getMinimumViolations(products, orderedTicketIds);
    if (violations.length === 0) {
      setPreCheckMinQuantityMessage("");
      return true;
    }

    const ticketNumberMap = getTicketNumberMap(orderedTicketIds);
    setPreCheckMinQuantityMessage(
      formatClientMinimumQuantityMessage(
        violations,
        ticketNumberMap,
        {
          single: r.ProductMinimumQuantityNotMetPerTicketSingle,
          plural: r.ProductMinimumQuantityNotMetPerTicket,
          and: r.And,
        },
      ),
    );
    openProductMinQuantityModal();
    return false;
  };

  const { createPayHandler, submittingButton } = useCreatePayHandler({
    handleSubmit: methods.handleSubmit,
    closePaymentModal,
    submitReservation,
    buildPayload,
    onError: handleSubmitError,
    preValidate: preValidateMinimums,
    beforeSubmit: async (values) => {
        void maybeUpdateUser(
            values.contact.mobile,
            values.contact.mobileCountryCode ?? "",
            values.saveInfoToAccount,
            values.contact.firstName,
            values.contact.lastName,
            values.contact.email,
        ).catch(() => { });
      },
  });

  useEffect(() => {
    if (!isUnder900) {
      closeOrderSummary();
    }
  }, [isUnder900]);

  useEffect(() => {
    if (
      status === OrderStatus.Canceled ||
      (isExpired && status === OrderStatus.Pending)
    ) {
      closePaymentModal();
      closeOrderSummary();
      requestCloseProductOverlays();
      setExpirationModalOpen(true);
    }
  }, [status, isExpired]);

  const billing = useWatch({
    control: methods.control,
    name: "billing",
  });

  const {
    payNowMethod,
    apiMethod,
    payLaterMethod,
    irisMethod,
    isPayNowActive,
    hasSecondaryPaymentOptions,
  } = usePaymentMethods(paymentMethods, total, billing?.type === "invoice");

  const handlePayNow = createPayHandler(
    "payNow",
    payNowMethod?.active ? (payNowMethod?.name ?? "") : (apiMethod?.name ?? ""),
  );
  const handlePayLater = createPayHandler(
    "payLater",
    payLaterMethod?.name ?? "",
  );
  const handlePayWithIris = createPayHandler("iris", irisMethod?.name ?? "");

  const loading = reservationLoading || userLoading;

  // Loading state
  if (loading) {
    return (
      <PageContainer>
        <ReservationSkeleton />
      </PageContainer>
    );
  }

  if (error || !data) {
    return <ErrorPage onRetry={onRetry} />;
  }

  const isDisabled = isSubmittingReservation || isRedirecting;

  return (
    <>
      <MoreTheme />
      {isOlympiacos && <OlympiacosTheme />}
      {isPaoBC && <PaobcTheme />}
      <SitePolicyPrompt
        intent="buy"
        mandatory={false}
        onBeforeLogin={markAsLeaving}
        onClose={() => {
          markAsLeaving();
          window.location.href = `${mainUrl()}/${getLocaleSegment()}/tickets/`;
        }}
        ref={sitePolicyPromptRef}
      />
      <FormProvider {...methods}>
        <PageContainer>
          <ReservationPageScopedGlobalStyle />
          <IntercomBelowModalsGlobalStyle />
          {isOlympiacos && <OlympiacosReservationScopedGlobalStyle />}
          <CountdownDisplay expiresAt={expiresAt} label={r.TimeRemaining} />
          <PageLayout>
            <LeftColumn>
              <TopContainer>
                <ContactInformation
                  userData={userData}
                  isLoggedIn={isLoggedIn}
                />

                {(ticketPersonFormFields.length > 0 ||
                  hasPerTicketProducts) && (
                  <TicketInfo
                    ticketPersonFormFields={ticketPersonFormFields}
                    tickets={tickets}
                    isKokEvent={isKokEvent}
                    isOlympiacosEvent={isOlympiacosEvent}
                    hasPerTicketProducts={hasPerTicketProducts}
                    currency={currency}
                  />
                )}

                {hasPerOrderProducts && (
                  <Products currency={currency} />
                )}

                {/* A per-ticket physical product still needs a delivery method
                    and an address, so this is gated on any product. */}
                {products.length > 0 && <ShippingAddress />}

                {donationActive && (
                  <Donations
                    donationCampaign={donationCampaign!}
                    tickets={tickets}
                  />
                )}
                {showBilling && <Billing />}
              </TopContainer>

              <BottomContainer>
                <Divider />

                <AgreementCheckboxes
                  merchantTerms={merchantTerms}
                  producerName={producerName ?? ""}
                />

              <Actions>
                <PaymentButtons
                  onPayNow={handlePayNow}
                  onPayLater={handlePayLater}
                  onIris={handlePayWithIris}
                  isSubmitting={isSubmittingReservation || submittingButton !== null}
                  submittingButton={submittingButton}
                  isFormDisabled={isDisabled}
                  isPayNowActive={isPayNowActive}
                  isPayLaterActive={payLaterMethod?.active}
                  isIrisActive={irisMethod?.active}
                  isFreeOrder={total === 0}
                  hasSecondaryOptions={hasSecondaryPaymentOptions}
                />
              </Actions>
            </BottomContainer>
          </LeftColumn>

            <RightColumn>
              <OrderInfo
                tickets={tickets}
                ticketsTotal={data?.totalPrice ?? 0}
                venue={venue}
                date={date}
                playTitle={playTitle}
                playUrl={playUrl}
                imageUrl={imageUrl}
                shippingFee={shippingFee}
                isUnder900={isUnder900}
                donationCampaign={donationCampaign}
                paidAmount={data?.paidAmount ?? 0}
              />
            </RightColumn>
          </PageLayout>

        <ReservationBottomBar
          isOrderSummaryOpen={isOrderSummaryOpen}
          onToggleOrderSummary={
            isOrderSummaryOpen ? closeOrderSummary : openOrderSummary
          }
          total={total}
          paidAmount={data?.paidAmount ?? 0}
          currency={currency}
          isDisabled={isDisabled}
          hasSecondaryPaymentOptions={hasSecondaryPaymentOptions}
          isSubmittingReservation={isSubmittingReservation}
          onPayNow={() => {
            closeOrderSummary();
            handlePayNow();
          }}
          onOpenPaymentModal={() => {
            closeOrderSummary();
            setPaymentModalKey((k) => k + 1);
            openPaymentModal();
          }}
        />

          {/* Drawer Start */}
          {isUnder900 && (
            <SwipeableDrawer
              anchor="bottom"
              open={isOrderSummaryOpen}
              onOpen={openOrderSummary}
              onClose={closeOrderSummary}
              disableSwipeToOpen
              disableScrollLock // page scroll is locked by useModal
              PaperProps={{ sx: { bottom: "var(--bottom-bar-height, 73px)" } }} // position drawer, so it doesnt overlap with BottomBar
            >
              <SummaryDrawerContent>
                <SummaryDrawerPuller />
                <SummaryDrawerScrollable>
                  <OrderInfo
                    tickets={tickets}
                    ticketsTotal={data?.totalPrice ?? 0}
                    venue={venue}
                    date={date}
                    playTitle={playTitle}
                    playUrl={playUrl}
                    imageUrl={imageUrl}
                    shippingFee={shippingFee}
                    isSummaryDrawer
                    donationCampaign={donationCampaign}
                    paidAmount={data?.paidAmount ?? 0}
                  />
                </SummaryDrawerScrollable>
              </SummaryDrawerContent>
            </SwipeableDrawer>
          )}

        <PaymentOverlay
          isOpen={isPaymentModalOpen}
          onClose={closePaymentModal}
          paymentModalKey={paymentModalKey}
          isUnder900={isUnder900}
          onPayNow={handlePayNow}
          onPayLater={handlePayLater}
          onIris={handlePayWithIris}
          isSubmitting={isSubmittingReservation}
          submittingButton={submittingButton}
          isFormDisabled={isDisabled}
          isPayNowActive={isPayNowActive}
          isPayLaterActive={payLaterMethod?.active}
          isIrisActive={irisMethod?.active}
          isFreeOrder={total === 0}
          hasSecondaryOptions={hasSecondaryPaymentOptions}
        />
        {/* Drawer End */}

          <ReservationModals
            expirationModalOpen={expirationModalOpen}
            onCloseExpiration={handleCloseExpirationModal}
            isProductsUpdatedModalOpen={isProductsUpdatedModalOpen}
            onCloseProductsUpdated={closeProductsUpdatedModal}
            isProductMinQuantityModalOpen={isProductMinQuantityModalOpen}
            onCloseProductMinQuantity={closeProductMinQuantityModal}
            productMinQuantityDescription={
              preCheckMinQuantityMessage || submitError?.message || ""
            }
            isEmailLimitModalOpen={isEmailLimitModalOpen}
            onCloseEmailLimit={closeEmailLimitModal}
            emailLimitDescription={submitError?.message ?? ""}
            emailLimitExceeded={(submitError?.emailLimitRemaining ?? 0) > 0}
            isLeaveModalOpen={isLeaveModalOpen}
            onCloseLeaveModal={closeLeaveModal}
            onLeave={handleLeave}
            isGenericErrorModalOpen={isGenericErrorModalOpen}
            onCloseGenericError={handleCloseGenericErrorModal}
          />
        </PageContainer>
      </FormProvider>
    </>
  );
}